import { _IdGenerator } from '@angular/cdk/a11y';
import { ChangeDetectionStrategy, Component, ElementRef, computed, inject, input, output, signal } from '@angular/core';
import { HdBodygraphPlanetaryActivation, HdBodygraphSide } from '../../hd-bodygraph.models';
import { HdChartHoverHighlightService } from '../../hd-chart-hover-highlight.service';
import { createHdGateHoverHighlight } from '../../hd-gate-hover-highlight';
import { HdContentLanguageService } from '../../hd-content-language.service';
import { HdPlanetInfoService } from '../../hd-planet-info.service';
import { HdFixingState, HdPlanetDisplayConfig } from '../hd-planet-display-config';
import { HdPlanetGridColorConfig } from '../hd-planet-grid-color-config';
import { getHdPlanetDisplayConfig } from '../hd-planet-display-config';
import { HdBaseBadgeState, HdNodeRole, resolveBaseBadgeState, resolveBaseHoverTarget, resolveHdFixingState, resolveHdNodeRole, rotateBaseBadgeStateForLps } from '../hd-planet-grid.utils';
import { toVeryDarkVariant } from '@shared/utils/color.utils';

/**
 * HSL-lichtheid van de tekst op een licht incarnatiekruis (amber van Whale):
 * gedefinieerd een zacht donker amber-bruin (~5:1 contrast i.p.v. 10:1 voor
 * zwart), in de inverse-stand op gebroken wit iets lichter.
 */
const LIGHT_CROSS_TEXT_LIGHTNESS = 0.2;
const LIGHT_CROSS_INVERSE_TEXT_LIGHTNESS = 0.35;

/**
 * Klik-event van een planet-grid blok, met cursor- of anchor-positie zodat
 * het floating-panel via smart-placement nabij het blok kan opengaan
 * (zie `docs/dialog-placement.md`). Voor toetsenbord-activatie wordt het
 * midden van het blok zelf als oorsprong gebruikt — `clientX/Y` is dan 0.
 */
export interface HdPlanetActivationBlockClickEvent {
  readonly activation: HdBodygraphPlanetaryActivation;
  readonly origin: { readonly x: number; readonly y: number };
}

export type HdPlanetBlockVariant = 'cross' | 'grid';

/**
 * Dichtheid van de planet-grid-blokken. `normal` is de bestaande 80px-weergave;
 * `mini` rendert een compact twee-regel-blok (regel 1 = planeetsymbool +
 * exaltatie/detriment-pijltjes, regel 2 = poort.lijn) van ±44px breed zodat
 * het design- én personality-grid naast elkaar op een telefoon passen.
 * Zie `docs/MOBIEL.md` §9 (extra punten).
 */
export type HdPlanetGridDensity = 'normal' | 'mini';

@Component({
  selector: 'hd-planet-activation-block',
  templateUrl: './hd-planet-activation-block.component.html',
  styleUrl: './hd-planet-activation-block.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    'role': 'button',
    'tabindex': '0',
    '(click)': 'emitBlockClicked($event)',
    '(keydown.enter)': 'emitBlockClicked($event)',
    '(keydown.space)': 'emitBlockClicked($event)',
    '(mouseenter)': 'onMouseEnter()',
    '(mouseleave)': 'onMouseLeave()',
    '(focus)': 'onFocus()',
    '(blur)': 'onBlur()',
    '[attr.data-gate]': 'activation().gate',
    '[attr.aria-describedby]': 'hdKeynoteVisible() ? hdKeynoteId : null',
    '[class.is-hover-match]': 'isHoverMatch()',
    '[class.is-hover-source]': 'isHoverSource()',
    '[class.is-hover-dimmed]': 'isHoverDimmed()',
    '[class.is-dim-disabled]': 'isDimDisabled()',
    '[class.density-mini]': 'isMini()',
    '[class.side-personality]': "side() === 'personality'",
  },
})
export class HdPlanetActivationBlockComponent {
  readonly activation = input.required<HdBodygraphPlanetaryActivation>();
  readonly blockClicked = output<HdPlanetActivationBlockClickEvent>();

  private readonly hostElementRef = inject(ElementRef<HTMLElement>);
  private readonly hoverService = inject(HdChartHoverHighlightService, { optional: true });
  private readonly contentLang = inject(HdContentLanguageService);
  private readonly planetInfoService = inject(HdPlanetInfoService);

  /**
   * Lokale "ik ben momenteel het gehoverde block-element" signal — er kunnen
   * meerdere blokken met dezelfde gate bestaan (LPS-grid, incarnatiekruis,
   * levenskruis), maar slechts één is de directe hover-source.
   */
  private readonly hostHovered = signal(false);
  private readonly hostFocused = signal(false);

  /** Gedeelde hover-regels (match / bron / dim), zie {@link createHdGateHoverHighlight}. */
  private readonly gateHover = createHdGateHoverHighlight(this.hoverService, () => this.activation().gate, this.hostHovered);
  protected readonly isHoverMatch = this.gateHover.isMatch;
  protected readonly isHoverSource = this.gateHover.isSource;
  protected readonly isHoverDimmed = this.gateHover.isDimmed;

  /**
   * "Dimmen bij hover" staat uit (app-setting): matches tonen dan een blauwe
   * rand (zie SCSS `:host.is-dim-disabled.is-hover-match`) omdat extra
   * activaties van dezelfde poort zonder dim niet meer vanzelf opvallen.
   */
  protected readonly isDimDisabled = computed(() =>
    this.hoverService != null && !this.hoverService.dimOnHover(),
  );

  protected onMouseEnter(): void {
    this.hostHovered.set(true);
    this.hoverService?.setKeynoteHover(this.activation().gate, 'planet-grid-block');
    this.hoverService?.setGateHover(this.activation().gate, 'planet-grid-block');
  }

  protected onMouseLeave(): void {
    this.hostHovered.set(false);
    this.hoverService?.setKeynoteHover(null, 'planet-grid-block');
    this.hoverService?.setGateHover(null, 'planet-grid-block');
  }

  protected onFocus(): void {
    this.hostFocused.set(true);
  }

  protected onBlur(): void {
    this.hostFocused.set(false);
  }

  /**
   * Hover-enter op het link-icoontje (alleen Personality South Node en
   * Design North Node). Publiceert een `gates`-kind hover-state met de
   * eigen node-gate plus alle opposite-side LPS-gates die dezelfde `line`
   * delen — daardoor lichten die LPS-blokken mee op via de bestaande
   * `is-hover-match`-flow. No-op wanneer er geen matches zijn (val terug
   * op de gewone gate-hover die de block-`mouseenter` al gezet heeft).
   * Rechterklik over het icoon bubbelt naar de planet-grid host; de
   * actieve gates-state wordt dan door `addCurrentHoverToFreeze()` in één
   * `gates`-item bevroren.
   */
  protected onLinkIconEnter(event: MouseEvent): void {
    event.stopPropagation();
    if (!this.hoverService?.enabled()) return;
    if (this.nodeRole() !== 'link') return;
    const matches = this.nodeLinkMatchGates();
    if (matches.length === 0) return;
    const gates = new Set<number>([this.activation().gate, ...matches]);
    this.hoverService.setGatesHover([...gates], 'planet-grid-block');
  }

  /**
   * Hover-leave op het link-icoontje: val terug op de gewone gate-hover
   * voor dit node-blok zolang de cursor nog binnen het blok zit. Het
   * blok-`mouseleave` ruimt vervolgens op wanneer de cursor het blok ook
   * verlaat. Geen no-op wanneer matches leeg waren — `setGateHover` is
   * idempotent en sluit netjes aan op de bestaande debounce-flow.
   */
  protected onLinkIconLeave(event: MouseEvent): void {
    event.stopPropagation();
    if (!this.hoverService?.enabled()) return;
    if (this.nodeRole() !== 'link') return;
    if (this.hostHovered()) {
      this.hoverService.setGateHover(this.activation().gate, 'planet-grid-block');
    } else {
      this.hoverService.setGateHover(null, 'planet-grid-block');
    }
  }

  /**
   * Hover-enter op het base-badge blokje (alleen Sun/Earth en Nodes).
   * Publiceert de bijhorende LPS-gate als gate-hover-state zodat het
   * corresponderende LPS-blok via de bestaande `is-hover-match`-flow
   * oplicht. Voor link-role nodes (PSN, DNN) zit die LPS-gate aan de
   * overkant — de mat-resolutie gebeurde reeds upstream in
   * `HdPlanetGridComponent`. No-op wanneer geen target-gate beschikbaar
   * is (val terug op de gewone gate-hover die de block-`mouseenter` al
   * gezet heeft). Zie `.\docs\human-design\base-logic.md`.
   */
  protected onBaseBadgeEnter(event: MouseEvent): void {
    event.stopPropagation();
    if (!this.hoverService?.enabled()) return;
    const targetGate = this.baseHoverTargetGate();
    if (targetGate == null) return;
    const ownGate = this.activation().gate;
    const gates = new Set<number>([ownGate, targetGate]);
    this.hoverService.setGatesHover([...gates], 'planet-grid-block');

    const target = resolveBaseHoverTarget(
      this.activation().base,
      this.displayConfig().key,
      this.activation().side,
    );
    if (target == null) return;
    const targetConfig = getHdPlanetDisplayConfig(target.planetKey);
    this.hoverService.setBaseHoverTarget({
      side: target.side,
      planetId: targetConfig.planetId,
      badgeState: this.baseBadgeState(),
      base: this.activation().base,
      quadrantColor: this.baseBadgeQuadrantColor(),
    });
  }

  /**
   * Hover-leave op het base-badge blokje: val terug op de gewone gate-hover
   * voor dit blok zolang de cursor nog binnen het blok zit (mirror-pattern
   * van {@link onLinkIconLeave}). Het blok-`mouseleave` ruimt vervolgens
   * op wanneer de cursor het blok ook verlaat.
   */
  protected onBaseBadgeLeave(event: MouseEvent): void {
    event.stopPropagation();
    if (!this.hoverService?.enabled()) return;
    if (this.baseHoverTargetGate() == null) return;
    if (this.hostHovered()) {
      this.hoverService.setGateHover(this.activation().gate, 'planet-grid-block');
    } else {
      this.hoverService.setGateHover(null, 'planet-grid-block');
    }
    this.hoverService.setBaseHoverTarget(null);
  }

  /**
   * Host-listener voor zowel LMB als Enter/Space toetsenbord-activatie.
   * Spec §1.3: planet-blok opent altijd het gate-info panel, ongeacht de
   * info/edit-modus van de chart.
   *
   * Voor een muisklik bevatten `clientX/Y` de cursor-positie; voor
   * toetsenbord-activatie zijn die 0 → fallback op het midden van het blok
   * zelf zodat smart-placement (zie `docs/dialog-placement.md`) een
   * zinnige oorsprong krijgt.
   */
  protected emitBlockClicked(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    // Ctrl+klik (Cmd+klik op macOS) is gereserveerd voor de freeze-toggle-
    // shortcut: niet-bevroren → toevoegen, al-bevroren → verwijderen. Wordt
    // ook hier behandeld zodat het bestaande planet-grid-block-klikgedrag
    // (info-panel openen) niet meeglijdt.
    if (event instanceof MouseEvent && (event.ctrlKey || event.metaKey)) {
      if (this.hoverService?.enabled()) {
        this.hoverService.toggleFreezeForTarget({ kind: 'gate', gate: this.activation().gate });
        return;
      }
    }
    this.blockClicked.emit({
      activation: this.activation(),
      origin: this.resolveClickOrigin(event),
    });
  }

  private resolveClickOrigin(event: Event): { x: number; y: number } {
    if (event instanceof MouseEvent && (event.clientX !== 0 || event.clientY !== 0)) {
      return { x: event.clientX, y: event.clientY };
    }
    const rect = this.hostElementRef.nativeElement.getBoundingClientRect();
    return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
  }

  readonly displayConfig = input.required<HdPlanetDisplayConfig>();
  readonly colorConfig = input.required<HdPlanetGridColorConfig>();
  readonly isDefined = input(true);
  readonly variant = input<HdPlanetBlockVariant>('cross');
  /**
   * Mini-densiteit (mobiel): twee regels — planeetsymbool + fixing-pijltjes,
   * daaronder poort.lijn. Alle kleuren (achtergrond/undefined-stijl/lijn-
   * highlights) blijven identiek; badges, gene keys, kleurcijfer en
   * node-role-iconen worden verborgen. Zie {@link HdPlanetGridDensity}.
   */
  readonly density = input<HdPlanetGridDensity>('normal');
  readonly geneKeysLabel = input<string | null>(null);
  /** Toon de langere planeet-keynote als hover-/focuskaart boven het blok. */
  readonly showHdKeynotes = input(false);
  /** Line numbers from the opposite-side node activations. LPS planets with a matching line get a yellow highlight. */
  readonly matchLines = input<ReadonlySet<number>>(new Set());
  /** Line numbers of the profile (Sun/Earth) from both sides. LPS planets with a matching line get a green highlight. */
  readonly profileLines = input<ReadonlySet<number>>(new Set());
  /** Line numbers of the nodes from the current side. Used to highlight nodes themselves in yellow. */
  readonly ownNodeLines = input<ReadonlySet<number>>(new Set());
  /**
   * Opposite-side LPS gate numbers that share the same `line` as this Node
   * activation. Only meaningful when {@link nodeRole} === `'link'`
   * (Personality South Node or Design North Node). Hovering the link icon
   * publishes a `gates`-kind hover-state covering the node's own gate plus
   * these gates, so the matching LPS blocks light up in the opposite
   * planet-grid. See `docs/human-design/hd-chart-hover-highlight.md`
   * (§ Link-node icon hover) and `docs/human-design/node-styles.md`.
   */
  readonly nodeLinkMatchGates = input<readonly number[]>([]);
  /**
   * LPS gate that should light up when hovering this block's base badge —
   * resolved upstream in `HdPlanetGridComponent` based on the activation's
   * base, planet key and side (Sun/Earth/Nodes only). For link-role nodes
   * (PSN, DNN) this gate lives on the opposite side; for all other
   * base-bearing planets it lives on the same side. `null` when this
   * block has no base badge or no matching LPS activation exists. See
   * `.\docs\human-design\base-logic.md` (§ Base Hover → LPS Highlight).
   */
  readonly baseHoverTargetGate = input<number | null>(null);

  /**
   * Zichtbare (bold) Resonance-keynote onder de poort.lijn.kleur-notatie
   * (advanced LPS). `null` → klassieke weergave (geen keynote, poort staat
   * onderaan). Zie `.\docs\human-design\hd-advanced-lunar-planetary-square.md`.
   */
  readonly resonanceKeynote = input<string | null>(null);
  /** Tooltip-tekst (klein lettertype) die bij hover over de Resonance-keynote toont. */
  readonly resonanceKeynoteTooltip = input<string | null>(null);
  /**
   * Base-nummer (1–5, "5 Dimensional Fields") dat als symbool in de hoek van
   * dit LPS-blok toont in de advanced LPS. `null` → geen base-symbool.
   */
  readonly resonanceBaseNumber = input<number | null>(null);
  /**
   * Asset-pad naar het HD-base-icoon (SVG) dat in de hoek van dit LPS-blok
   * toont. Wanneer gezet vervangt het icoon het cijfer-badge. `null` → toon
   * het cijfer-badge (fallback).
   */
  readonly resonanceBaseIconSrc = input<string | null>(null);
  /** Tooltip-tekst (naam + keynote) bij hover over het Resonance-base-symbool. */
  readonly resonanceBaseTooltip = input<string | null>(null);
  /**
   * Hoek waarin het Resonance-base-symbool getoond wordt. Personality toont
   * rechtsboven; design gebruikt een afwijkende plaatsing (linksboven) zodat
   * het niet botst met de gespiegelde leesrichting.
   */
  readonly resonanceBasePlacement = input<'top-right' | 'top-left'>('top-right');

  protected readonly isMini = computed(() => this.density() === 'mini');

  protected readonly showResonanceKeynote = computed(() => !this.isMini() && this.resonanceKeynote() !== null);
  protected readonly showResonanceBase = computed(() => !this.isMini() && this.resonanceBaseNumber() !== null);

  /** Gene keys-label vervalt in mini-densiteit — daar is geen ruimte voor. */
  protected readonly visibleGeneKeysLabel = computed<string | null>(() =>
    this.isMini() ? null : this.geneKeysLabel(),
  );

  protected readonly hdKeynote = computed<string | null>(() => {
    if (!this.showHdKeynotes()) return null;
    const subtitle = this.planetInfoService
      .planetInfoIn(this.displayConfig().key, this.contentLang.effectiveLang())
      .subtitle
      .trim();
    return subtitle || null;
  });

  protected readonly useForcedHdKeynoteBreak = computed(() =>
    this.displayConfig().key === 'mars' && this.contentLang.effectiveLang() === 'nl',
  );

  protected readonly hdKeynoteDisplayLines = computed<readonly string[]>(() => {
    const keynote = this.hdKeynote();
    if (keynote === null) return [];
    if (!this.useForcedHdKeynoteBreak()) return [keynote];

    const word = '(On)volwassenheid';
    const wordStart = keynote.indexOf(word);
    if (wordStart < 0) return [keynote];

    const splitIndex = wordStart + '(On)volwassen'.length;
    return [
      keynote.slice(0, wordStart).trimEnd(),
      `${keynote.slice(wordStart, splitIndex)}-`,
      keynote.slice(splitIndex),
    ];
  });

  protected readonly useCompactHdKeynoteText = computed(() =>
    this.displayConfig().key === 'mercury' && this.contentLang.effectiveLang() === 'en',
  );

  private readonly isMatchingKeynoteHover = computed(() => {
    const keynoteHover = this.hoverService?.keynoteHover();
    if (keynoteHover?.source === 'planet-grid-block') {
      return keynoteHover.gate === this.activation().gate;
    }
    if (this.hoverService?.keynotesAtHoverOnly() ?? true) return false;
    if (keynoteHover?.source !== 'chart-gate') return false;
    if (keynoteHover.kind === 'channel') {
      return keynoteHover.gates().has(this.activation().gate);
    }
    return keynoteHover.gate === this.activation().gate;
  });

  protected readonly hdKeynoteVisible = computed(() =>
    (this.hostHovered() || this.hostFocused() || this.isMatchingKeynoteHover())
      && this.hdKeynote() !== null,
  );

  protected readonly hdKeynoteId = inject(_IdGenerator).getId('hd-planet-keynote-');

  protected readonly side = computed<HdBodygraphSide>(() => this.activation().side);

  protected readonly isLineHighlighted = computed(() => {
    const config = this.displayConfig();
    const key = config.key;
    const line = this.activation().line;

    // 1. Yellow highlighting (Nodes and Node Matches)
    if (key === 'northNode' || key === 'southNode') return true;
    if (config.gridSection === 'lunarPlanetarySquare' && this.matchLines().has(line)) return true;

    // 2. Green highlighting (Profile and Profile Resonance)
    if (key === 'sun' || key === 'earth') return true;
    if (config.gridSection === 'lunarPlanetarySquare' && this.profileLines().has(line)) return true;

    return false;
  });

  protected readonly fixingState = computed<HdFixingState>(() =>
    resolveHdFixingState(this.activation()),
  );

  protected readonly nominalBackgroundColor = computed(() => {
    const config = this.displayConfig();
    const colors = this.colorConfig();
    const side = this.side();

    if (config.gridSection === 'incarnationCross') {
      return colors.incarnationCross[side];
    }

    if (config.gridSection === 'lifeCross') {
      if (colors.nodeBackgroundStyle === 'classic-red') {
        return colors.lifeCross.design;
      }
      return colors.lifeCross[side];
    }

    // LPS block: moon has a special extra-light color
    if (config.key === 'moon') {
      return this.isLineHighlighted() ? colors.lpsMoonHighlighted : colors.lpsMoon;
    }

    return colors.lpsColumn[config.lpsColumn!];
  });

  protected readonly isInverseStyle = computed(() =>
    !this.isDefined() && (this.colorConfig().undefinedStyle === 'inverse' || this.colorConfig().undefinedStyle === 'inverse-border' || this.colorConfig().undefinedStyle === 'inverse-border-hatch'),
  );

  protected readonly backgroundColor = computed(() => {
    if (this.isInverseStyle()) {
      return this.colorConfig().undefinedBackgroundColor;
    }
    return this.nominalBackgroundColor();
  });

  /**
   * Incarnatiekruis op een lichte achtergrond (amber van Whale): vraagt
   * donkere tekst en de donkere highlight-varianten, zie
   * {@link HdPlanetGridColorConfig.incarnationCrossTextTone}.
   */
  private readonly isLightCrossBackground = computed(() =>
    this.displayConfig().gridSection === 'incarnationCross'
    && this.colorConfig().incarnationCrossTextTone === 'dark',
  );

  protected readonly isDarkBackground = computed(() => {
    if (this.isInverseStyle()) {
      return false;
    }
    // Planeetblokken hebben een donkere achtergrond (witte tekst), behalve een
    // incarnatiekruis dat de preset als licht markeert.
    return !this.isLightCrossBackground();
  });

  /**
   * Tekst en rand in de inverse-stand: de blokkleur op gebroken wit. Een lichte
   * blokkleur (amber) wordt donkerder gemaakt, anders leest ze niet op wit.
   */
  private readonly inverseTextColor = computed(() => {
    const nominal = this.nominalBackgroundColor();
    return this.isLightCrossBackground() ? toVeryDarkVariant(nominal, LIGHT_CROSS_INVERSE_TEXT_LIGHTNESS) : nominal;
  });

  protected readonly defaultTextColor = computed(() => {
    if (this.isInverseStyle()) {
      return this.inverseTextColor();
    }
    if (this.isLightCrossBackground()) {
      // Zachter dan zwart: donker amber-bruin uit de blokkleur zelf (Steven 2026-10-18).
      return toVeryDarkVariant(this.nominalBackgroundColor(), LIGHT_CROSS_TEXT_LIGHTNESS);
    }
    return this.isDarkBackground() ? 'white' : 'black';
  });

  protected readonly showColorNumber = computed(() =>
    this.variant() === 'grid' && !this.isMini(),
  );

  protected readonly undefinedStyle = computed(() => this.colorConfig().undefinedStyle);

  protected readonly showUndefinedBorder = computed(() =>
    !this.isDefined() && (this.undefinedStyle() === 'opacity-border' || this.undefinedStyle() === 'hatch-notch-border' || this.undefinedStyle() === 'hatch-border' || this.undefinedStyle() === 'notch-border' || this.undefinedStyle() === 'border-only'),
  );

  protected readonly showUndefinedNotch = computed(() =>
    !this.isDefined() && (this.undefinedStyle() === 'corner-notch' || this.undefinedStyle() === 'hatch-notch-border' || this.undefinedStyle() === 'notch-border'),
  );

  protected readonly showUndefinedHatch = computed(() =>
    !this.isDefined() && (this.undefinedStyle() === 'hatch-overlay' || this.undefinedStyle() === 'hatch-notch-border' || this.undefinedStyle() === 'hatch-border' || this.undefinedStyle() === 'inverse-border-hatch'),
  );

  protected readonly hatchColor = computed(() => {
    if (!this.isInverseStyle()) {
      return this.colorConfig().undefinedHatchColor;
    }
    // In inverse mode the background is light, so the hatch must use the block's
    // own color at 20% opacity to remain visible while staying tonally consistent.
    return toRgba(this.nominalBackgroundColor(), 0.20);
  });

  protected readonly opacity = computed(() =>
    !this.isDefined() && this.undefinedStyle() === 'opacity-border'
      ? this.colorConfig().undefinedOpacity
      : 1,
  );

  protected readonly blockBorder = computed(() =>
    this.isInverseStyle() ? `1px solid ${this.defaultTextColor()}` : null,
  );

  protected readonly keynoteBorder = computed(() => {
    const blockBorder = this.blockBorder();
    if (blockBorder) return blockBorder;
    if (this.showUndefinedBorder()) {
      return `1px solid ${this.colorConfig().undefinedInnerBorderColor}`;
    }
    return 'none';
  });

  protected readonly showBaseBadge = computed(() => {
    if (this.isMini()) return false;
    const key = this.displayConfig().key;
    return key === 'sun' || key === 'earth' || key === 'northNode' || key === 'southNode';
  });

  protected readonly showExaltationBadge = computed(() => {
    const state = this.fixingState();
    return state === 'exaltation' || state === 'both';
  });

  protected readonly showDetrimentBadge = computed(() => {
    const state = this.fixingState();
    return state === 'detriment' || state === 'both';
  });

  protected readonly showBothFixingBadge = computed(() =>
    this.fixingState() === 'both',
  );

  /**
   * "Echo" base-badge: gevuld wanneer dit blok het LPS-doelplaneetblok is
   * waar een momenteel-gehoverde source base-badge naar wijst. Wordt apart
   * van {@link baseBadgeState} gerenderd zodat dezelfde planet-block in
   * principe naast zijn eigen base-badge (indien aanwezig) ook een echo
   * kan tonen — in de praktijk hebben LPS-planeten geen eigen base-badge,
   * dus is er nooit overlap. Inclusief de source's base-waarde voor
   * volledige visuele eenduidigheid.
   */
  protected readonly echoBaseBadge = computed<{ state: HdBaseBadgeState; base: number; quadrantColor: string } | null>(() => {
    if (this.isMini()) return null;
    const target = this.hoverService?.baseHoverTarget();
    if (!target) return null;
    const activation = this.activation();
    if (activation.side !== target.side) return null;
    if (activation.planetId !== target.planetId) return null;
    return { state: target.badgeState, base: target.base, quadrantColor: target.quadrantColor };
  });

  protected readonly isEchoBaseFull = computed(() => {
    const echo = this.echoBaseBadge();
    if (!echo) return false;
    const s = echo.state;
    return s.tl && s.tr && s.bl && s.br;
  });

  protected readonly baseBadgeState = computed<HdBaseBadgeState>(() => {
    const classic = resolveBaseBadgeState(
      this.activation().base,
      this.displayConfig().key,
      this.activation().side,
    );
    return this.colorConfig().baseRotation === 'lps'
      ? rotateBaseBadgeStateForLps(classic)
      : classic;
  });

  /**
   * Color of the filled base-badge quadrants (and the partial-badge border).
   *
   * In `classic` rotation this stays the historical design red. In `lps`
   * rotation the badge instead adopts the color of the Lunar Planetary Square
   * block it points to — so the base orientation visually reads "this points
   * to that LPS planet". The Moon target gets its lighter Moon blue; every
   * other target (Pluto/Uranus/Saturn/Mars, all in the prana column) gets the
   * standard LPS blue. See `.\docs\human-design\base-logic.md`.
   */
  protected readonly baseBadgeQuadrantColor = computed(() => {
    const colors = this.colorConfig();
    if (colors.baseRotation !== 'lps') {
      return colors.baseBadge.quadrantColor;
    }
    const target = resolveBaseHoverTarget(
      this.activation().base,
      this.displayConfig().key,
      this.activation().side,
    );
    if (target == null) {
      return colors.baseBadge.quadrantColor;
    }
    if (target.planetKey === 'moon') {
      return colors.lpsMoon;
    }
    const targetConfig = getHdPlanetDisplayConfig(target.planetKey);
    return targetConfig.lpsColumn != null
      ? colors.lpsColumn[targetConfig.lpsColumn]
      : colors.baseBadge.quadrantColor;
  });

  /**
   * Node-role for this activation:
   *  - `prime` → Personality North Node or Design South Node (anchor icon).
   *  - `link`  → Personality South Node or Design North Node (link/crossover icon).
   *  - `null`  → not a Node activation.
   */
  protected readonly nodeRole = computed<HdNodeRole>(() =>
    resolveHdNodeRole(this.displayConfig().key, this.activation().side),
  );

  protected readonly nodeRoleIconClass = computed<string | null>(() => {
    if (this.isMini()) return null;
    switch (this.nodeRole()) {
      case 'prime': return 'fa-solid fa-anchor';
      case 'link': return 'fa-solid fa-link';
      default: return null;
    }
  });

  /**
   * Hover-text for the Prime/Link node indicator. Localised via the HD content
   * language (NL / EN). Prime node tooltip mentions "design" on the design side
   * and "personality" on the personality side.
   */
  protected readonly nodeRoleTooltip = computed<string | null>(() => {
    const role = this.nodeRole();
    if (!role) return null;
    const lang = this.contentLang.effectiveLang();
    const isDesignSide = this.activation().side === 'design';

    if (role === 'link') {
      return lang === 'nl'
        ? 'Link node: lijm van de magnetische monopool tussen personaliteit en design, dat naar de andere kant wisselt op vlak van base orientatie.'
        : 'Link node: glue of the magnetic monopole between personality and design, crossing over to the other side in their base orientation.';
    }

    // prime
    if (lang === 'nl') {
      const subject = isDesignSide ? 'het design' : 'de personaliteit';
      return `Prime node: verankert de interne essentie van ${subject} in de buitenwereld dmv de eigen base orientatie.`;
    }
    const subject = isDesignSide ? 'design' : 'personality';
    return `Prime node: anchors the internal essence of the ${subject} to the external world through it's own base orientation.`;
  });

  protected readonly isBaseFull = computed(() => {
    const s = this.baseBadgeState();
    return s.tl && s.tr && s.bl && s.br;
  });

  /**
   * True when the visible planet block background equals the base badge quadrant color.
   * In that case the default red border on a partial badge would be invisible, so it must be forced white.
   */
  protected readonly baseBadgeNeedsContrastBorder = computed(() =>
    this.backgroundColor() === this.baseBadgeQuadrantColor(),
  );

  /**
   * Echo variant of {@link baseBadgeNeedsContrastBorder}: the echo badge sits
   * on the target LPS block and can share that block's background color (e.g. a
   * Moon-blue echo on the Moon block), in which case the partial-badge border
   * must be forced white to stay visible.
   */
  protected readonly echoBaseBadgeNeedsContrastBorder = computed(() => {
    const echo = this.echoBaseBadge();
    return echo != null && this.backgroundColor() === echo.quadrantColor;
  });

  protected readonly colorNumberColor = computed(() => {
    if (this.isInverseStyle()) {
      const side = this.side();
      const colors = this.colorConfig();
      // For cross blocks in inverse style, we might want the highlight color if it's highlighted
      if (this.displayConfig().gridSection === 'incarnationCross') {
        return colors.colorHighlight[side];
      }
      return this.nominalBackgroundColor();
    }
    const isDark = this.isDarkBackground();
    const side = this.side();
    const colors = this.colorConfig();

    if (this.displayConfig().gridSection === 'lunarPlanetarySquare') {
      return isDark ? 'white' : 'black';
    }

    return isDark ? colors.colorHighlight[side] : colors.colorHighlightDark[side];
  });

  protected readonly colorNumberOutlineColor = computed(() => {
    if (!this.isInverseStyle() || this.displayConfig().gridSection !== 'incarnationCross') {
      return null;
    }
    const colors = this.colorConfig();
    const side = this.side();
    return colors.colorHighlightDark[side];
  });

  protected readonly lineColor = computed(() => {
    const config = this.displayConfig();
    const colors = this.colorConfig();
    const key = config.key;
    const side = this.side();
    const line = this.activation().line;
    const isDark = this.isDarkBackground();
    const isInverse = this.isInverseStyle();

    // 1. Yellow highlighting (Nodes and Node Matches)
    // - Nodes themselves (P or D)
    if (key === 'northNode' || key === 'southNode') {
      return (isDark || isInverse) ? colors.lineHighlight.design : colors.lineHighlightDark.design;
    }
    // - LPS matches with opposite side nodes
    if (config.gridSection === 'lunarPlanetarySquare' && this.matchLines().has(line)) {
      const matchSide = side === 'design' ? 'personality' : 'design';
      return (isDark || isInverse) ? colors.nodeMatchLineHighlight[matchSide] : colors.nodeMatchLineHighlightDark[matchSide];
    }

    // 2. Green highlighting (Profile and Profile Resonance)
    // - Sun/Earth (Profile lines)
    if (key === 'sun' || key === 'earth') {
      return (isDark || isInverse) ? colors.lineHighlight.personality : colors.lineHighlightDark.personality;
    }
    // - LPS matches with profile lines (P-Sun or D-Sun)
    if (config.gridSection === 'lunarPlanetarySquare' && this.profileLines().has(line)) {
      return (isDark || isInverse) ? colors.lineHighlight.personality : colors.lineHighlightDark.personality;
    }

    // 3. Inverse style non-highlighted color
    if (isInverse) {
      return this.nominalBackgroundColor();
    }

    // 4. Default (White or Black depending on background)
    if (config.gridSection === 'lunarPlanetarySquare') {
      return isDark ? 'white' : 'black';
    }

    // Default for cross blocks
    return colors.lineHighlight.personality;
  });

  protected readonly lineOutlineColor = computed(() => {
    if (!this.isInverseStyle() || !this.isLineHighlighted()) {
      return null;
    }
    const config = this.displayConfig();
    const colors = this.colorConfig();
    const key = config.key;
    const side = this.side();
    const line = this.activation().line;

    if (key === 'northNode' || key === 'southNode') {
      return colors.lineHighlightDark.design;
    }
    if (config.gridSection === 'lunarPlanetarySquare' && this.matchLines().has(line)) {
      const matchSide = side === 'design' ? 'personality' : 'design';
      return colors.nodeMatchLineHighlightDark[matchSide];
    }
    return colors.lineHighlightDark.personality;
  });

  protected readonly lineShadow = computed(() => {
    const color = this.lineOutlineColor();
    if (!color) return null;

    if (this.colorConfig().undefinedStyle === 'inverse-border' || this.colorConfig().undefinedStyle === 'inverse-border-hatch') {
      return `-1px -1px 0 ${color}, 1px -1px 0 ${color}, -1px 1px 0 ${color}, 1px 1px 0 ${color}`;
    }

    // Soft shadow effect instead of hard outline
    return `0.5px 0.5px 1.5px ${color}`;
  });

  protected readonly colorNumberShadow = computed(() => {
    const color = this.colorNumberOutlineColor();
    if (!color) return null;

    if (this.colorConfig().undefinedStyle === 'inverse-border' || this.colorConfig().undefinedStyle === 'inverse-border-hatch') {
      return `-1px -1px 0 ${color}, 1px -1px 0 ${color}, -1px 1px 0 ${color}, 1px 1px 0 ${color}`;
    }

    // Soft shadow effect instead of hard outline
    return `0.5px 0.5px 1.5px ${color}`;
  });
}

/** Convert an `rgb(r, g, b)` or `rgba(r, g, b, a)` string to `rgba(r, g, b, alpha)`. */
function toRgba(color: string, alpha: number): string {
  const match = color.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
  if (!match) return color;
  return `rgba(${match[1]}, ${match[2]}, ${match[3]}, ${alpha})`;
}
